import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Site } from '../../lib/sites';
import { organisationsApi } from '../../lib/organisations';
import type { ControllerStateInput } from '../site-controllers/SiteControllerStateBadge';
import SiteControllerStateBadge from '../site-controllers/SiteControllerStateBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';

interface SitesListProps {
  sites: Site[];
  controllersBySite?: Record<string, ControllerStateInput[]>;
}

export default function SitesList({
  sites,
  controllersBySite,
}: SitesListProps) {
  const navigate = useNavigate();

  const { data: organisations } = useQuery({
    queryKey: ['organisations'],
    queryFn: () => organisationsApi.getAll(),
  });

  const organisationNameById = useMemo(() => {
    const map = new Map<string, string>();
    (organisations ?? []).forEach((o) => map.set(o.id, o.name));
    return map;
  }, [organisations]);

  const handleRowClick = (siteId: string) => {
    navigate(`/sites/${siteId}`);
  };
  if (sites.length === 0) {
    return (
      <Card className="text-center py-12" data-testid="sites-list-empty">
        <p className="text-muted-foreground">No sites available</p>
      </Card>
    );
  }

  return (
    <Card data-testid="sites-list-container">
      <div className="overflow-x-auto" data-testid="sites-list-scroll-container">
        <Table data-testid="sites-table" id="sites-table">
          <TableHeader data-testid="sites-table-header">
            <TableRow>
              <TableHead className="px-3 sm:px-4 md:px-6" data-testid="sites-table-header-name">
                Name
              </TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6" data-testid="sites-table-header-organisation">
                Organisation
              </TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6" data-testid="sites-table-header-address">
                Address
              </TableHead>
              <TableHead className="hidden md:table-cell px-3 sm:px-4 md:px-6" data-testid="sites-table-header-description">
                Description
              </TableHead>
              <TableHead className="px-3 sm:px-4 md:px-6 whitespace-nowrap text-right" data-testid="sites-table-header-controllers">
                Controllers
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="sites-table-body">
            {sites.map((site) => {
              const organisationName = site.organisationId
                ? organisationNameById.get(site.organisationId)
                : undefined;

              return (
                <TableRow 
                  key={site.id} 
                  data-testid={`sites-table-row-${site.id}`} 
                  id={`site-row-${site.id}`}
                  onClick={() => handleRowClick(site.id)}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="px-3 sm:px-4 md:px-6 max-w-0" data-testid={`site-name-${site.id}`}>
                    <div className="truncate" title={site.name}>
                      {site.name}
                    </div>
                    {site.description && (
                      <div className="md:hidden mt-1 text-xs text-muted-foreground truncate" title={site.description} data-testid={`site-description-mobile-${site.id}`}>
                        {site.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-3 sm:px-4 md:px-6 max-w-0" data-testid={`site-organisation-${site.id}`}>
                    <div className="truncate" title={organisationName}>
                      {organisationName ?? '-'}
                    </div>
                  </TableCell>
                  <TableCell className="px-3 sm:px-4 md:px-6 max-w-0" data-testid={`site-address-${site.id}`}>
                    <div className="truncate" title={site.address || undefined}>
                      {site.address || '-'}
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell px-3 sm:px-4 md:px-6 max-w-0" data-testid={`site-description-${site.id}`}>
                    <div className="truncate" title={site.description || undefined}>
                      {site.description || '-'}
                    </div>
                  </TableCell>
                  <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap" data-testid={`site-controllers-${site.id}`}>
                    <SiteControllerStateBadge
                      controllers={controllersBySite?.[site.id] || []}
                      wrapperClassName="flex items-center justify-end gap-2"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
